import { keys, update, isEmpty } from "lodash";

import {
  REQUEST_PURCHASE_ORDERS_SUCCESS,
  REQUEST_PURCHASE_ORDER_ITEMS_SUCCESS,
  UPDATE_PURCHASE_ORDER_PRODUCT,
  REQUEST_PRODUCT_OF_GTIN_SUCCESS,
  CREATE_PURCHASE_ORDER_SUCCESS,
  MARK_AS_RECEIVED_SUCCESS,
  REQUEST_NEW_PURCHASE_ORDER_ITEMS_SUCCESS,
  CLEAR_NEW_PURCHASE_ORDER_PRODUCTS,
  UPDATE_PURCHASE_ORDER_PRODUCT_QUANTITY,
  REQUEST_PURCHASE_ORDER_ITEMS_INVENTORY_SUCCESS,
  REQUEST_REMOVE_PURCHASE_ORDER_ITEM,
} from "../../constants/actionTypes";

const INITIAL_STATE = {
  purchaseOrders: [],
  products: [],
  inventory: {},
  productsList: [],
  importedBatchIds: [],
};

export default function purchaseOrders(state = INITIAL_STATE, action) {
  const {
    purchaseOrders = [],
    products = [],
    inventory = {},
  } = action?.payload?.data || {};

  switch (action.type) {
    case REQUEST_PURCHASE_ORDERS_SUCCESS:
      return {
        ...state,
        purchaseOrders,
      };

    case CLEAR_NEW_PURCHASE_ORDER_PRODUCTS:
      return {
        ...state,
        productsList: [],
        importedBatchIds: [],
      };

    case REQUEST_NEW_PURCHASE_ORDER_ITEMS_SUCCESS:
      const newProductsList = [...state.productsList];
      const newProductsPayload = action.payload.productsList || [];
      const fetchedBtachId = action.payload.batchId;

      newProductsPayload.forEach((newProduct) => {
        const existingProductIndex = newProductsList.findIndex(
          (p) => p.id === newProduct.id
        );

        if (existingProductIndex !== -1) {
          const existing = newProductsList[existingProductIndex];
          // Keep every batch garment id when merging same SKU rows.
          const mergedIds = [
            ...new Set(
              [
                ...(existing.batchItemIds || []),
                ...(existing.batchItemId ? [existing.batchItemId] : []),
                ...(newProduct.batchItemIds || []),
                ...(newProduct.batchItemId ? [newProduct.batchItemId] : []),
              ]
                .filter(Boolean)
                .map(String)
            ),
          ];
          const mergeIdList = (...groups) => [
            ...new Set(
              groups
                .flat()
                .filter(Boolean)
                .map(String)
            ),
          ];
          existing.batchItemIds = mergedIds;
          if (!existing.batchItemId && mergedIds[0]) {
            existing.batchItemId = mergedIds[0];
          }
          existing.sourceItemIds = mergeIdList(
            existing.sourceItemIds,
            existing.sourceItemId,
            newProduct.sourceItemIds,
            newProduct.sourceItemId
          );
          existing.orderIds = mergeIdList(
            existing.orderIds,
            existing.orderId,
            newProduct.orderIds,
            newProduct.orderId
          );
          existing.orderItemIds = mergeIdList(
            existing.orderItemIds,
            existing.orderItemId,
            newProduct.orderItemIds,
            newProduct.orderItemId
          );
          // Prefer unique batch-item count when present (1 unit per doc).
          if (mergedIds.length > 0) {
            existing.quantity = mergedIds.length;
          } else {
            existing.quantity =
              (Number(existing.quantity) || 0) +
              (Number(newProduct.quantity) || 0);
          }
          existing.stock = {
            ...existing.stock,
            required: existing.quantity,
          };
        } else {
          const batchItemIds = [
            ...new Set(
              [
                ...(newProduct.batchItemIds || []),
                ...(newProduct.batchItemId ? [newProduct.batchItemId] : []),
              ]
                .filter(Boolean)
                .map(String)
            ),
          ];
          newProductsList.push({
            ...newProduct,
            batchItemIds,
            sourceItemIds: [
              ...new Set(
                [
                  ...(newProduct.sourceItemIds || []),
                  newProduct.sourceItemId,
                ]
                  .filter(Boolean)
                  .map(String)
              ),
            ],
            orderIds: [
              ...new Set(
                [...(newProduct.orderIds || []), newProduct.orderId]
                  .filter(Boolean)
                  .map(String)
              ),
            ],
            orderItemIds: [
              ...new Set(
                [
                  ...(newProduct.orderItemIds || []),
                  newProduct.orderItemId,
                ]
                  .filter(Boolean)
                  .map(String)
              ),
            ],
            quantity:
              batchItemIds.length > 0
                ? batchItemIds.length
                : Number(newProduct.quantity) || 0,
            stock: {
              ...(newProduct.stock || {}),
              required:
                batchItemIds.length > 0
                  ? batchItemIds.length
                  : Number(newProduct.quantity) || 0,
            },
          });
        }
      });

      return {
        ...state,
        productsList: newProductsList,
        importedBatchIds: fetchedBtachId
          ? [
              ...new Set(
                [...state.importedBatchIds, String(fetchedBtachId)].filter(
                  Boolean
                )
              ),
            ]
          : state.importedBatchIds,
      };

    case UPDATE_PURCHASE_ORDER_PRODUCT_QUANTITY:
      const { productId, quantity } = action.payload;
      const updatedProductsList = state.productsList.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity,
            stock: { ...product.stock, required: quantity },
          };
        }
        return product;
      });

      return {
        ...state,
        productsList: updatedProductsList,
      };

    case REQUEST_PURCHASE_ORDER_ITEMS_SUCCESS:
      return {
        ...state,
        products,
        inventory,
      };

    case REQUEST_PURCHASE_ORDER_ITEMS_INVENTORY_SUCCESS:
      const updatedInventory = {
        ...state.inventory,
        ...Object.keys(inventory).reduce((acc, blankProductId) => {
          acc[blankProductId] = {
            ...(state.inventory[blankProductId] || {}),
            ...inventory[blankProductId],
          };
          return acc;
        }, {}),
      };

      const productsListWithSupplier = (state.productsList || []).map(
        (product) => {
          const row =
            updatedInventory?.[product.blankProductId]?.[product.blankVariantId] ||
            updatedInventory?.[product.blankProductId]?.[
              String(product.blankVariantId)
            ];
          const resolvedId = row?.resolvedSupplierId;
          if (!resolvedId) return product;
          const current = String(product.supplierId || "");
          const isMasterSupplier = !current || /^riverr$/i.test(current);
          if (!isMasterSupplier) return product;
          return {
            ...product,
            supplierId: resolvedId,
            supplierName: row.resolvedSupplierName || product.supplierName,
          };
        }
      );

      return {
        ...state,
        inventory: updatedInventory,
        productsList: productsListWithSupplier,
      };

    case REQUEST_PRODUCT_OF_GTIN_SUCCESS:
      let newInv = state?.inventory || {};
      const inventoryPayload = action?.payload?.data?.inventory || {};

      keys(inventoryPayload).forEach((bpId) => {
        update(newInv, `${bpId}`, (n) =>
          isEmpty(n)
            ? { ...inventoryPayload[bpId] }
            : { ...n, ...inventoryPayload[bpId] }
        );
      });

      return {
        ...state,
        inventory: newInv,
      };

    case UPDATE_PURCHASE_ORDER_PRODUCT:
      const { product: oldProduct = {}, newProduct = {} } =
        action.payload || {};
      const updatedProductsListForUpdate = state.productsList.map((product) => {
        if (product.id === oldProduct.id) {
          return {
            ...newProduct,
            stock: { ...product.stock, required: newProduct.quantity },
          };
        }
        return product;
      });

      return {
        ...state,
        productsList: updatedProductsListForUpdate,
      };
    case REQUEST_REMOVE_PURCHASE_ORDER_ITEM: {
      return {
        ...state,
        productsList: state.productsList.filter(
          (product) => product.supplierId !== action.payload
        ),
      };
    }

    case CREATE_PURCHASE_ORDER_SUCCESS:
      const productsAfterCreate = state?.products.filter(
        (f) => f?.supplierId !== action.payload?.supplierId
      );

      return {
        ...state,
        products: productsAfterCreate,
      };

    case MARK_AS_RECEIVED_SUCCESS: {
      const receivedPo = action?.payload?.data?.purchaseOrder || {};
      const receivedId = receivedPo?.id;
      const afterMarkReceived = (state?.purchaseOrders || []).map((po) => {
        if (receivedId && po?.id === receivedId) {
          return {
            ...po,
            ...receivedPo,
            received: receivedPo.received ?? true,
            status: receivedPo.status || "Received",
          };
        }
        return po;
      });

      return {
        ...state,
        purchaseOrders: afterMarkReceived,
      };
    }

    default:
      return state;
  }
}
